import React from 'react';
import { Body5, Container, H5, Page } from '@ui';
import { DocumentIcon } from '@assets/icons/svgs/DocumentIcon';
import { CloseButton } from '@ui/buttons/CloseButton';
import { addAdvancedSettingValues, useAdvancedSettings } from '@hooks/application/useAdvancedSettings';
import { sendApiDownloadRequest } from '@services/api';
import { submitTemplate } from '@services/genaiQueriesV2';
import { includesId, logNetRequest } from '@utils/usefulFuncs';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { Spinner } from '@ui/LoadingSpinner';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { FlatButton } from '@ui/buttons';
import { FormState } from '@ui/FormState';
import styled from 'styled-components';
import { AI_TOOLS, DOC_SELECT_MODES, DOCUMENT_TEMPLATE_MODE_IDS } from '@constants';
import { Working } from '@ui/Working';
import { StandaloneDocSelectModal } from '@containers/docSelection/StandaloneDocSelectModal';
import Complete from '@ui/Complete';
import { AdvancedSettingsButton } from '../AdvancedSettings';
import { useExtraModeParams } from '@hooks/features/useExtraModeParams';
import { useStreamStatus } from '@hooks/utils/useStreamStatus';
import { ExtraModeParams } from './ExtraModeParams';

const PageWrapper = styled.div`
  + div {
    width: 100%;
  }
`;
const SubmitButtonWrapper = styled.div`
  text-align: center;
  width: 100%;
`;
const FormWrapper = styled.div`
  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 0 !important;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 100px;
    width: 100%;
    padding: 16px;
  }
`;
const SelectedDocumentWrapper = styled.div``;
const IconWrapper = styled.div`
  margin-right: 8px;

  svg {
    padding-top: 1px;
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;
const CloseButtonWrapper = styled.div`
  margin-left: 8px;

  svg {
    padding-top: 1px;
    height: 12px;
    width: 12px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;

const defaultDocSelectModes = [DOC_SELECT_MODES.allCompany, DOC_SELECT_MODES.docType];

const modeIdToUserModeConversion = (modeId) => {
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.peer_benchmarking_template) return AI_TOOLS.PEER_BENCHMARKING_TEMPLATE;
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.quarterly_data_template) return AI_TOOLS.QUARTERLY_DATA_TEMPLATE;
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.earnings_grid_template) return AI_TOOLS.EARNINGS_GRID_TEMPLATE;
  return modeId;
};
const generateHeadingText = (modeId) => {
  if (!modeId) return null;
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.peer_benchmarking_template) return 'Peer Benchmarking';
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.quarterly_data_template) return 'Quarterly Data Template';
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.earnings_grid_template) return 'Earnings Grid';
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.portfolio_monitoring_template) return 'Portfolio Monitoring';
  return 'Download Tool - Mode missing name';
};
const generateDescriptionComponent = (modeId) => {
  if (!modeId) return null;
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.quarterly_data_template) return null;
  return null;
};
function SelectedDocument({ doc, handleRemove }) {
  if (!doc) return null;

  const { filename } = doc;
  return (
    <SelectedDocumentWrapper>
      <Container row verticallyCenter>
        <IconWrapper>
          <DocumentIcon />
        </IconWrapper>
        <Container>
          <Body5>{filename}</Body5>
        </Container>
        <CloseButtonWrapper>
          <CloseButton onClick={() => handleRemove(doc)} />
        </CloseButtonWrapper>
      </Container>
    </SelectedDocumentWrapper>
  );
}
export function ReportDownloadTool({ streamStatus, onDownloadDocument, onClear }) {
  if (streamStatus === 'pending') {
    return (
      <Container>
        <Working message="Generating report..." />
        <Container top={16} row verticallyCenter>
          <Container>
            <FlatButton disabled onClick={() => null} fullWidth>
              Download Document
            </FlatButton>
          </Container>
          <Container left={16}>
            <SkeletonButton onClick={onClear}>Close</SkeletonButton>
          </Container>
        </Container>
        <Container top={16}>
          <Body5>Download available when report generation is complete</Body5>
        </Container>
      </Container>
    );
  }
  if (streamStatus === 'error') {
    return (
      <Container>
        <Body5>Error generating report, please try again.</Body5>
      </Container>
    );
  }
  if (streamStatus === 'complete') {
    return (
      <Container>
        <Container>
          <Complete message="Report generation is complete" />
        </Container>
        <Container top={24}>
          <Body5>Click the button below to download the results in a spreadsheet.</Body5>
        </Container>
        <Container top={16} row verticallyCenter>
          <Container>
            <FlatButton
              disabled={streamStatus !== 'complete'}
              onClick={streamStatus === 'complete' ? onDownloadDocument : () => null}
              fullWidth
            >
              Download Document
            </FlatButton>
          </Container>
          <Container left={16}>
            <SkeletonButton onClick={onClear}>Close</SkeletonButton>
          </Container>
        </Container>
      </Container>
    );
  }
  return null;
}
function modeParams(modeId) {
  if (modeId === DOCUMENT_TEMPLATE_MODE_IDS.quarterly_data_template) {
    return {
      use_formula_dependency_context: true,
    };
  }

  return {};
}

function convertMonthYearStringToLastDate(str) {
  if (!str) return undefined;
  const [monStr, yearStr] = str.split(' ');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIdx = monthNames.indexOf(monStr);
  const year = parseInt(yearStr, 10);
  if (monthIdx === -1 || isNaN(year)) return undefined;
  // The 0th day of the next month is the last day of the given month
  const date = new Date(year, monthIdx + 1, 0);
  // Return as yyyy-mm-dd
  return date.toISOString().slice(0, 10);
}

function convertMonthDayStringToDate(str) {
  if (!str) return undefined;
  const year = 2024;
  const [monthStr, dayStr] = str.split(' ');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIdx = monthNames.indexOf(monthStr);
  const day = parseInt(dayStr, 10);
  if (monthIdx === -1 || isNaN(day)) return undefined;
  const date = new Date(year, monthIdx, day);
  return date.toISOString().slice(0, 10);
}
function convertModeParamsToApiFormat(modeParams) {
  const output = {};
  Object.entries(modeParams).forEach(([key, value]) => {
    if (key.includes('_section_header')) {
      return;
    }
    output[key] = value;
    if (['fiscal_quarter_end_date', 'quarter_end_date', 'fiscal_year_end_date'].includes(key)) {
      output[key] = convertMonthYearStringToLastDate(value);
    }
    if (key === 'calendar_date') {
      output[key] = convertMonthYearStringToLastDate(value);
    }
    if (key === 'convert_to_usd') {
      output[key] = value === 'true';
    }
  });
  return output;
}

export function DocumentDownloadTool({ onGoBack, modeId, tool }) {
  const advancedSettingsValues = useAdvancedSettings();
  const { model } = advancedSettingsValues;

  const tickersRef = React.useRef([]);
  const [docSelectModes] = React.useState(defaultDocSelectModes);
  const [isSelectingDocs, setIsSelectingDocs] = React.useState(false);
  const [selectedDocs, setSelectedDocs] = React.useState([]);

  const [isSubmittingToApi, setIsSubmittingToApi] = React.useState(false);
  const [formError, setFormError] = React.useState(null);

  const [resultId, setResultId] = React.useState(null);
  const streamStatus = useStreamStatus(resultId);
  const { modeExtraParams, state: modeExtraParamsState, onChangeModeExtraParams } = useExtraModeParams(modeId);

  React.useEffect(() => {
    if (resultId) {
      const id = 'snapshot-answers-section';
      const yOffset = -26;
      const element = document.getElementById(id);
      if (element) {
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 300);
      }
    }
  }, [resultId]);

  const onSubmit = async () => {
    if (selectedDocs.length === 0) {
      setFormError('Please select at least one document');
      return;
    }

    setFormError(null);
    setIsSubmittingToApi(true);
    setResultId(null);

    const docIds = selectedDocs.map((d) => d.id);
    const settings = { user_mode: modeIdToUserModeConversion(modeId), model, ...modeParams(modeId) };
    addAdvancedSettingValues(advancedSettingsValues, settings);
    const modeParamsBody =
      modeExtraParams.length > 0 ? convertModeParamsToApiFormat({ ...modeExtraParamsState }) : undefined;
    const result = await submitTemplate({
      modeType: modeId,
      docIds,
      modeParams: modeParamsBody,
      settings,
    });
    if (result.status === 200) {
      setResultId(result.runId);
    } else {
      setFormError('Something went wrong, try again');
    }
    setIsSubmittingToApi(false);
  };

  const onDownloadDocument = async () => {
    try {
      const filename = 'GeneratedReport';
      const URL = `v1/genai/templates/filled/${modeId}/0?template_run_id=${resultId}`;
      const response = await sendApiDownloadRequest(URL);
      const { status, data, error } = response;
      logNetRequest(status, data, URL);
      if (status === 200) {
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setFormError(error || 'Something went wrong downloading the file, please try again later.');
      }
    } catch (error) {
      setFormError(error || 'Something went wrong downloading the file, please try again later.');
      console.error('Error downloading the file:', error);
    }
  };

  const onClear = () => {
    setSelectedDocs([]);
    setResultId(null);
    setFormError(null);
  };

  const handleAddDocument = (v) => {
    if (Array.isArray(v)) {
      setSelectedDocs((prevState) => [...prevState, ...v]);
    } else {
      setSelectedDocs((prevState) => [...prevState, v]);
    }
  };

  const handleRemoveDocument = (filenameOrFilenames) => {
    if (Array.isArray(filenameOrFilenames)) {
      setSelectedDocs((prevState) => prevState.filter((d) => !includesId(filenameOrFilenames, d.id)));
    } else {
      setSelectedDocs((prevState) => prevState.filter((d) => d.id !== filenameOrFilenames.id));
    }
  };

  const onSelectDocumentsClick = () => setIsSelectingDocs(true);

  const handleClearSnapshot = () => {
    onClear();
  };

  return (
    <PageWrapper>
      <Page width={resultId ? '1800px' : '800px'}>
        <BackArrowButton onClick={onGoBack} />
        <AdvancedSettingsButton tool={tool} />
        <Container top={40}>
          <H5>{generateHeadingText(modeId)}</H5>
          {generateDescriptionComponent(modeId)}
        </Container>
        {isSelectingDocs && (
          <StandaloneDocSelectModal
            docSelectModes={docSelectModes}
            defaultActiveMode={docSelectModes[0]}
            tool={tool}
            tickersRef={tickersRef}
            selectedDocs={selectedDocs}
            handleAddDocument={handleAddDocument}
            handleRemoveDocument={handleRemoveDocument}
            onDismiss={() => setIsSelectingDocs(false)}
          />
        )}
        {isSubmittingToApi ? (
          <Container top={80} bottom={80} centerAll>
            <Container>
              <Container centerAll>
                <Spinner />
              </Container>
              <Container top={24} left={12}>
                <Body5>Working...</Body5>
              </Container>
            </Container>
          </Container>
        ) : (
          <Container top={8}>
            <FormWrapper>
              <Container top={selectedDocs.length > 0 ? 16 : 0}>
                {selectedDocs.map((d, i) => (
                  <Container key={d.id} top={0} bottom={8}>
                    <SelectedDocument doc={d} handleRemove={handleRemoveDocument} />
                  </Container>
                ))}
                <Container top={24}>
                  {selectedDocs.length > 0 ? (
                    <SkeletonButton onClick={onSelectDocumentsClick}>+ Select Documents</SkeletonButton>
                  ) : (
                    <FlatButton onClick={onSelectDocumentsClick}>+ Select Documents</FlatButton>
                  )}
                </Container>
              </Container>
              <ExtraModeParams
                top={32}
                modeExtraParams={modeExtraParams}
                modeExtraParamsState={modeExtraParamsState}
                onChangeModeExtraParams={onChangeModeExtraParams}
              />

              <Container top={48}>
                <Container centerAll>
                  <FormState error={formError} />
                </Container>
                <SubmitButtonWrapper>
                  <FlatButton disabled={selectedDocs.length === 0} onClick={onSubmit} fullWidth>
                    Create Report
                  </FlatButton>
                </SubmitButtonWrapper>
              </Container>
            </FormWrapper>
          </Container>
        )}
      </Page>
      {resultId && (
        <Page width={'1800px'}>
          <ReportDownloadTool
            streamStatus={streamStatus}
            onDownloadDocument={onDownloadDocument}
            onClear={handleClearSnapshot}
          />
        </Page>
      )}
    </PageWrapper>
  );
}
